import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../components/LanguageSelector/translations';
import { adminApi } from '../services/api';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

interface ShippingCost {
  id: string;
  nameSlug: string;
  cost: number;
  minOrderAmount: number | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  countries: string[];
  createdAt: string;
  updatedAt: string;
}

const ShippingCostsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('NL');

  const countryNames: { [key: string]: string } = {
    'NL': 'Netherlands',
    'BE': 'Belgium', 
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy'
  };

  useEffect(() => {
    loadShippingCosts();
  }, []);

  const loadShippingCosts = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getPublicShippingCosts();
      setShippingCosts(response);
    } catch (error) {
      console.error('Error loading shipping costs:', error);
      setError(t.shippingCosts.errorLoading || 'Error loading shipping costs');
    } finally {
      setLoading(false);
    }
  };

  const getShippingCostsForCountry = (country: string) => {
    return shippingCosts.filter(cost => 
      cost.isActive && cost.countries.includes(country.toUpperCase())
    );
  };

  const formatPrice = (cost: number) => {
    if (cost === 0) {
      return t.shippingCosts.free || 'Free';
    }
    return `€${cost.toFixed(2)}`;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t.shippingCosts.loading || 'Loading...'}</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const availableCountries = Array.from(new Set(shippingCosts.flatMap(cost => cost.countries)));
  const filteredCosts = getShippingCostsForCountry(selectedCountry);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t.shippingCosts.title || 'Shipping Costs'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.shippingCosts.subtitle || 'Discover our shipping options and delivery times for your location'}
            </p>
          </div>

          {/* Shipping Costs */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {t.shippingCosts.availableOptions || 'Available Shipping Options'}
              </h2>
              <div className="text-sm text-gray-500">
                {countryNames[selectedCountry]} ({selectedCountry})
              </div>
            </div>

            {filteredCosts.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-gray-600 text-lg">
                  {t.shippingCosts.noOptions || 'No shipping options available for this country'}
                </p>
                <p className="text-gray-500 mt-2">
                  {t.shippingCosts.contactUs || 'Please contact us for shipping options'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredCosts
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((cost) => (
                    <div
                      key={cost.id}
                      className={`border rounded-lg p-6 transition-colors ${
                        cost.isDefault
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {t.shipping[cost.nameSlug as keyof typeof t.shipping] || cost.nameSlug}
                            </h3>
                            {cost.isDefault && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {t.shippingCosts.default || 'Default'}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-gray-600 mb-3">
                            {t.shipping[cost.nameSlug + '_desc' as keyof typeof t.shipping] || 
                             t.shippingCosts.noDescription || 'No description available'}
                          </p>
                          
                          {cost.minOrderAmount && (
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              <span>
                                {t.shippingCosts.minOrderAmount || 'Minimum order amount'}: €{cost.minOrderAmount.toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right ml-6">
                          <div className="text-2xl font-bold text-gray-900">
                            {formatPrice(cost.cost)}
                          </div>
                          {cost.cost === 0 && cost.minOrderAmount && (
                            <div className="text-sm text-gray-500 mt-1">
                              {t.shippingCosts.aboveMinAmount || 'Above minimum amount'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8 max-w-2xl mx-auto">
          <div className="flex items-start space-x-3">
            <svg className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                {t.shippingCosts.importantInfo || 'Important Information'}
              </h3>
              <ul className="text-blue-800 space-y-1">
                <li>• {t.shippingCosts.info3 || 'Delivery times may vary depending on your location'}</li>
                <li>• {t.shippingCosts.info4 || 'Contact us for special shipping arrangements'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ShippingCostsPage;
